import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import mainAxios from '../../services/mainAxios';
import { useAppContext } from '../../AppContext';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const CisMessage = ({ transactionType, objectType, requestBody }) => {
  const { t } = useTranslation('translations');
  const [syncLogData, setSyncLogData] = useState({ loading: false, data: null });
  const overlayRef = useRef(null);
  const {
    selectedCompany: [selectedCompany],
    user: [user]
  } = useAppContext();

  // Determine which mode: new (transactionType) or legacy (requestBody/objectType)
  const isLegacyMode = !transactionType && (requestBody || objectType);

  const legacyContent = useMemo(() => {
    const cisMsg = syncLogData?.data;
    if (!cisMsg) return null;
    console.log('Legacy CIS Message:', cisMsg);
    return (
      <div style={{ padding: '10px', minWidth: '320px', maxWidth: '450px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
          {cisMsg.recordDate && (
            <div style={{ display: 'flex' }}>
              <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.cisInfo')} Last Update:</span>
              <span style={{ marginLeft: '10px' }}>{moment(cisMsg.recordDate).format('DD/MM/YYYY HH:mm')}</span>
            </div>
          )}
          {cisMsg.schedulerNextExecution && (
            <div style={{ display: 'flex' }}>
              <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.cisInfo')} Next Update:</span>
              <span style={{ marginLeft: '10px' }}>{moment(cisMsg.schedulerNextExecution).format('DD/MM/YYYY HH:mm')}</span>
            </div>
          )}
          {cisMsg.retryNextExecutionTime && (
            <div style={{ display: 'flex' }}>
              <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.cisInfo')} Retry:</span>
              <span style={{ marginLeft: '10px' }}>{moment(cisMsg.retryNextExecutionTime).format('DD/MM/YYYY HH:mm')}</span>
            </div>
          )}
          {cisMsg.created >= 0 && (
            <div style={{ display: 'flex' }}>
              <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.cisInfo')} Created:</span>
              <span style={{ marginLeft: '10px' }}>{cisMsg.created}</span>
            </div>
          )}
          {cisMsg.modified >= 0 && (
            <div style={{ display: 'flex' }}>
              <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.cisInfo')} Modified:</span>
              <span style={{ marginLeft: '10px' }}>{cisMsg.modified}</span>
            </div>
          )}
        </div>
      </div>
    );
  }, [syncLogData, t]);

  const syncContent = useMemo(() => {
    if (isLegacyMode) return legacyContent;

    const syncLog = syncLogData?.data;
    if (!syncLog) return null;

    const { lastAttempt, lastSuccessful } = syncLog;

    // If no sync logs exist, display a message
    if (!lastAttempt && !lastSuccessful) {
      return (
        <div style={{ padding: '10px', minWidth: '320px', maxWidth: '450px' }}>
          <div style={{ fontWeight: 600, color: '#6c757d', marginBottom: '8px', fontSize: '1em' }}>
            ℹ {t('cisSync.noSyncLogs')}
          </div>
          <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
            {t('cisSync.noSyncLogsFound')}
          </div>
        </div>
      );
    }

    return (
      <div style={{ padding: '10px', minWidth: '320px', maxWidth: '450px' }}>
        {lastSuccessful && (
          <div style={{ marginBottom: lastAttempt && !lastAttempt.successful ? '12px' : '0' }}>
            <div style={{ fontWeight: 600, color: '#28a745', marginBottom: '8px', fontSize: '1em' }}>
              ✓ {t('cisSync.lastSuccessfulAttempt')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.date')}:</span>
                <span style={{ marginLeft: '10px' }}>{moment(lastSuccessful.recordDate).format('DD/MM/YYYY HH:mm')}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.user')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastSuccessful.recordUser || 'N/A'}</span>
              </div>
              {(lastSuccessful.fromDate && lastSuccessful.toDate) && (
                <div style={{ display: 'flex' }}>
                  <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.period')}:</span>
                  <span style={{ marginLeft: '10px' }}>{moment(lastSuccessful.fromDate).format('DD/MM/YYYY')} - {moment(lastSuccessful.toDate).format('DD/MM/YYYY')}</span>
                </div>
              )}
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.foundInCis')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastSuccessful.invoicesFoundInCis || 0}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.processed')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastSuccessful.totalInvoicesProcessed || 0}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.saved')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastSuccessful.invoicesSaved || 0}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.updated')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastSuccessful.invoicesUpdated || 0}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.toBeVerified')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastSuccessful.invoicesToBeVerified || 0}</span>
              </div>
              {lastSuccessful.nextScheduledExecution && (
                <div style={{ display: 'flex', marginTop: '4px' }}>
                  <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.nextExecution')}:</span>
                  <span style={{ marginLeft: '10px' }}>{moment(lastSuccessful.nextScheduledExecution).format('DD/MM/YYYY HH:mm')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {lastAttempt && !lastAttempt.successful && (
          <div style={{
            paddingTop: lastSuccessful ? '12px' : '0',
            borderTop: lastSuccessful ? '1px solid #ddd' : 'none'
          }}>
            <div style={{ fontWeight: 600, color: '#dc3545', marginBottom: '8px', fontSize: '1em' }}>
              ✗ {t('cisSync.lastAttemptFailed')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.9em' }}>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.date')}:</span>
                <span style={{ marginLeft: '10px' }}>{moment(lastAttempt.recordDate).format('DD/MM/YYYY HH:mm')}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.user')}:</span>
                <span style={{ marginLeft: '10px' }}>{lastAttempt.recordUser || 'N/A'}</span>
              </div>
              {(lastAttempt.fromDate && lastAttempt.toDate) && (
                <div style={{ display: 'flex' }}>
                  <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.period')}:</span>
                  <span style={{ marginLeft: '10px' }}>{moment(lastAttempt.fromDate).format('DD/MM/YYYY')} - {moment(lastAttempt.toDate).format('DD/MM/YYYY')}</span>
                </div>
              )}
              {lastAttempt.message && (
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 600, width: '130px' }}>{t('cisSync.error')}:</span>
                  <span style={{ color: '#dc3545', marginLeft: '10px', flex: 1 }}>{lastAttempt.message}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }, [syncLogData, t, isLegacyMode, legacyContent]);

  const fetchData = () => {
    setSyncLogData({ loading: true, data: null });

    if (isLegacyMode) {
      // Legacy mode: use requestBody or construct from objectType
      mainAxios
        .post('apiEndpoint/search', requestBody ?? {
          object: "GetInvoicesRequestLogV",
          params: { type: objectType },
          company: selectedCompany,
        })
        .then(res => {
          if (res?.status === 200 && res?.data?.content?.[0]) {
            setSyncLogData({ loading: false, data: res.data.content[0] });
          } else {
            setSyncLogData({ loading: false, data: null });
          }
        })
        .catch(err => {
          console.error('Error fetching CIS info:', err);
          setSyncLogData({ loading: false, data: null });
        });
    } else if (transactionType) {
      // New mode: use transactionType
      mainAxios
        .post('/apiEndpoint/search', {
          object: "GetInvoiceSyncLog",
          params: { transactionType },
          company: selectedCompany,
          username: user.username,
        })
        .then(res => {
          if (res?.data?.status === true && res?.data?.content?.[0]) {
            setSyncLogData({ loading: false, data: res.data.content[0] });
          } else {
            setSyncLogData({ loading: false, data: null });
          }
        })
        .catch(err => {
          console.error('Error fetching invoice sync log:', err);
          setSyncLogData({ loading: false, data: null });
        });
    }
  };

  useEffect(() => {
    if (transactionType || isLegacyMode) {
      fetchData();
    } else {
      setSyncLogData({ loading: false, data: null });
    }
  }, [transactionType, selectedCompany]);

  if (!transactionType && !isLegacyMode) {
    return null;
  }

  return (
    <>
      <button
        className={`btn btn-sm ${
          !isLegacyMode && syncLogData?.data?.lastAttempt && !syncLogData.data.lastAttempt.successful
            ? 'btn-outline-warning'
            : 'btn-outline-success'
        } ml-2`}
        onClick={(e) => overlayRef.current?.toggle(e)}
        disabled={!syncContent}
      >
        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
        {t('cisSync.cisInfo')}
      </button>
      <OverlayPanel ref={overlayRef}>
        {syncContent}
      </OverlayPanel>
    </>
  );
};

export default CisMessage;
